import { Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { isAIFCStagingAreaView } from '@databases/database-state';
import { isMobile } from '@databases/modes';
import shouldShowUpdatedSidePanel from '@databases/panel/shouldShowUpdatedSidePanel';
import React, { type FunctionComponent } from 'react';
import { Panel } from 'react-resizable-panels';
import styled, { css } from 'styled-components';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import PanelSizes from './panel/sizes';

const ToolbarContainer = styled.div<{
  isMobile: boolean;
  isEmbedView: boolean;
  isNativeEmbedView: boolean;
  isContentWrapperEnabled: boolean;
  isRightGutterCollapsed: boolean;
  showUpdatedSidePanel: boolean;
  isSidePanelVisible: boolean;
  isVisualRefreshM1Enabled: boolean;
  isReadOnly: boolean;
  isInAIFCStagingAreaView: boolean;
}>`
  ${(prop) => {
    if (prop.isMobile) {
      return css`
        padding-left: ${token('space.100', '8px')};
        padding-right: ${token('space.100', '8px')};
      `;
    }

    if (prop.isEmbedView) {
      return css`
        padding-left: 0;
      `;
    }

    if (prop.isNativeEmbedView) {
      return css`
        padding-left: 0;
      `;
    }

    if (prop.isVisualRefreshM1Enabled) {
      if (prop.isReadOnly) {
        return css`
          padding-left: ${token('space.300', '24px')};
        `;
      }
      if (prop.isRightGutterCollapsed || prop.isSidePanelVisible) {
        return css`
          padding: 0 0 0 ${token('space.600', '48px')};
        `;
      }

      if (prop.isInAIFCStagingAreaView) {
        return css`
          padding-right: ${token('space.600', '48px')};
          padding-left: ${token('space.600', '48px')};
        `;
      }
      return css`
        padding-right: ${token('space.800', '64px')};
        padding-left: ${token('space.600', '48px')};
      `;
    }
    if (prop.isContentWrapperEnabled) {
      if (prop.isRightGutterCollapsed || prop.isSidePanelVisible) {
        return css`
          padding: 0 0 0 ${token('space.800')};
        `;
      }
      if (prop.isInAIFCStagingAreaView) {
        return css`
          padding-right: ${token('space.600', '48px')};
          padding-left: ${token('space.600', '48px')};
        `;
      }
      return css`
        padding: 0 ${token('space.800')};
      `;
    }

    if (prop.showUpdatedSidePanel) {
      return css`
        padding-left: ${token('space.025')};
      `;
    }
    return css`
      padding-left: ${token('space.200')};
    `;
  }}
`;

export const ToolbarRenderer = ({
  isEmbedView,
  isNativeEmbedView,
  isContentWrapperEnabled,
  isRightGutterCollapsed,
  isSidePanelVisible,
  toolbar,
  isReadOnly,
}: {
  isEmbedView: boolean;
  isNativeEmbedView: boolean;
  isContentWrapperEnabled: boolean;
  isRightGutterCollapsed: boolean;
  isSidePanelVisible: boolean;
  toolbar: React.ReactNode;
  isReadOnly?: boolean;
}) => {
  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );
  const showUpdatedSidePanel = shouldShowUpdatedSidePanel();
  const isInAIFCStagingAreaView = isAIFCStagingAreaView();
  return (
    <ToolbarContainer
      isMobile={isMobile()}
      isEmbedView={isEmbedView}
      isNativeEmbedView={isNativeEmbedView}
      isContentWrapperEnabled={isContentWrapperEnabled}
      isRightGutterCollapsed={isRightGutterCollapsed}
      showUpdatedSidePanel={showUpdatedSidePanel}
      isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
      isSidePanelVisible={isSidePanelVisible}
      isReadOnly={isReadOnly}
      isInAIFCStagingAreaView={isInAIFCStagingAreaView}
      id="layout-toolbar-wrapper"
      data-testid="layout-toolbar-wrapper"
    >
      {toolbar}
    </ToolbarContainer>
  );
};

const renderViewWrapper = (BaseComponent: 'div' | typeof Panel) => styled(BaseComponent)<{
  $isMobile: boolean;
  $isEmbed: boolean;
  $isNativeEmbedView: boolean;
  $isContentWrapperEnabled: boolean;
  $isRightGutterCollapsed: boolean;
  $isVisualRefreshM1Enabled: boolean;
  $isInAIFCStagingAreaView: boolean;
}>`
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  height: 100%;

  ${(props) => {
    if (props.$isMobile) {
      return css`
        padding-left: ${token('space.0', '0px')};
        padding-right: ${token('space.0', '0px')};
        max-width: 100vw;
      `;
    }

    if (props.$isEmbed) {
      return css`
        padding-left: ${token('space.100', '8px')};
        padding-right: ${token('space.100', '8px')};
      `;
    }

    if (props.$isNativeEmbedView) {
      return css`
        padding-left: ${token('space.100', '8px')};
        padding-right: ${token('space.100', '8px')};
      `;
    }

    if (props.$isVisualRefreshM1Enabled) {
      if (props.$isRightGutterCollapsed) {
        return css`
          padding: 0 0 0 ${token('space.300', '24px')};
        `;
      }

      if (props.$isInAIFCStagingAreaView) {
        return css`
          padding-left: ${token('space.200', '16px')};
          padding-right: ${token('space.600', '48px')};
        `;
      }

      return css`
        max-width: calc(
          100vw - ${token('space.800', '64px')} - ${token('space.300', '24px')}
        );
        padding-left: ${token('space.300', '24px')};
      `;
    }

    if (props.$isContentWrapperEnabled) {
      if (props.$isRightGutterCollapsed) {
        return css`
          padding: 0 0 0 ${token('space.800', '64px')};
        `;
      }

      if (props.$isInAIFCStagingAreaView) {
        return css`
          padding-right: ${token('space.600', '48px')};
          padding-left: ${token('space.600', '48px')};
        `;
      }
      return css`
        max-width: calc(100vw - ${token('space.800', '64px')} * 2);
        padding-left: ${token('space.800', '64px')};
      `;
    }
    return css`
      padding-left: ${token('space.0')};
    `;
  }}
`;

export interface MainPanelProps {
  isEmbedView: boolean;
  isNativeEmbedView: boolean;
  isContentWrapperEnabled: boolean;
  isRightGutterCollapsed: boolean;
  isSidePanelVisible: boolean;
  options: {
    toolbarLocation: 'top' | 'views';
  };
  view: React.ReactNode;
  toolbar: React.ReactNode;
  isReadOnly?: boolean;
}

const ViewWrapper = renderViewWrapper('div');
const ViewWrapperPanel = renderViewWrapper(Panel);

/**
 * Renders the main (left) panel of DatabaseLayout
 */
const MainPanel: FunctionComponent<MainPanelProps> = ({
  isEmbedView,
  isNativeEmbedView,
  isContentWrapperEnabled,
  isRightGutterCollapsed,
  isSidePanelVisible,
  options,
  view,
  toolbar,
  isReadOnly,
}) => {
  const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
    'databases-frontend-visual-refresh-table-m1'
  );
  const showUpdatedSidePanel = shouldShowUpdatedSidePanel();
  const isInAIFCStagingAreaView = isAIFCStagingAreaView();
  const isMobileApp = isMobile();

  return showUpdatedSidePanel ? (
    <Panel
      order={PanelSizes.main.order}
      maxSize={PanelSizes.main.max}
      minSize={PanelSizes.main.min}
      id="main"
    >
      <Stack
        xcss={xcss({
          height: '100%',
        })}
      >
        {options.toolbarLocation === 'top' ? (
          <ToolbarRenderer
            isEmbedView={isEmbedView}
            isNativeEmbedView={isNativeEmbedView}
            isContentWrapperEnabled={isContentWrapperEnabled}
            isRightGutterCollapsed={isRightGutterCollapsed}
            isSidePanelVisible={isSidePanelVisible}
            toolbar={toolbar}
            isReadOnly={isReadOnly}
          />
        ) : null}
        <ViewWrapper
          $isMobile={isMobileApp}
          $isEmbed={isEmbedView}
          $isNativeEmbedView={isNativeEmbedView}
          $isContentWrapperEnabled={isContentWrapperEnabled}
          $isRightGutterCollapsed={isRightGutterCollapsed}
          $isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
          $isInAIFCStagingAreaView={isInAIFCStagingAreaView}
          data-testid="layout-view--wrapper"
        >
          {options.toolbarLocation === 'views' ? (
            <ToolbarRenderer
              isEmbedView={isEmbedView}
              isNativeEmbedView={isNativeEmbedView}
              isContentWrapperEnabled={isContentWrapperEnabled}
              isRightGutterCollapsed={isRightGutterCollapsed}
              isSidePanelVisible={isSidePanelVisible}
              toolbar={toolbar}
            />
          ) : null}
          {view}
        </ViewWrapper>
      </Stack>
    </Panel>
  ) : (
    <ViewWrapperPanel
      order={PanelSizes.main.order}
      maxSize={PanelSizes.main.max}
      minSize={PanelSizes.main.min}
      id="main"
      $isMobile={isMobileApp}
      $isEmbed={isEmbedView}
      $isContentWrapperEnabled={isContentWrapperEnabled}
      $isRightGutterCollapsed={isRightGutterCollapsed}
      $isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
      data-testid="layout-view--wrapper-panel"
    >
      {options.toolbarLocation === 'views' ? (
        <ToolbarRenderer
          isEmbedView={isEmbedView}
          isNativeEmbedView={isNativeEmbedView}
          isContentWrapperEnabled={isContentWrapperEnabled}
          isRightGutterCollapsed={isRightGutterCollapsed}
          toolbar={toolbar}
          isSidePanelVisible={isSidePanelVisible}
        />
      ) : null}
      {view}
    </ViewWrapperPanel>
  );
};

export default MainPanel;
